<template>
  <component
    :is="href ? 'a' : 'button'"
    :class="cssClasses"
    :href="href"
    @click="onClick"
    :target="target"
    :type="props.form ? 'submit' : null"
    :disabled="isDisabled"
  >
    <component
      v-if="displayedIconLeft"
      :is="displayedIconLeft"
      :extra-classes="extraIconClasses"
    />
    {{ text }}

    <slot></slot>
    <component
      v-if="displayedIconRight"
      :is="displayedIconRight"
      :extra-classes="extraIconClasses"
    />
  </component>
</template>

<script setup lang="ts">
  import Spinner from '@/components/icons/Spinner.vue';
  import { computed } from 'vue';
  import { useFormsStore } from '@/stores/useFormsStore';

  const formsStore = useFormsStore()

  const props = defineProps({
    type: {
      type: String,
      default: 'primary',
    },
    submit: {
      type: Boolean,
    },
    text: {
      type: String,
      required: true,
    },
    iconRight: {
      type: String,
    },
    iconLeft: {
      type: String,
    },
    iconClasses: {
      type: Array<string>,
      default: () => [],
    },
    form: {
      type: String,
    },
    extraClasses: {
      type: Array<string>,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disableOnSubmit: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: '',
    },
    target: {
      type: String,
      default: '_self',
    },
  })

  const emits = defineEmits<{
    click: [event: MouseEvent]
  }>()

  const onClick = (event: MouseEvent) => emits("click", event);

  const isSubmitting = computed(() => {
    return props.form && formsStore.isFormSubmitting(props.form)
  })

  const cssClasses = computed(() => {
    let classes = ['button', `button--${props.type}`]

    if (props.extraClasses) {
      classes = classes.concat(props.extraClasses)
    }

    if (isDisabled.value) {
      classes.push('button--disabled')
    }

    if (isSubmitting.value) {
      classes.push('button--submitting')
    }
    return classes.join(' ')
  })

  const displayedIconLeft = computed(() => {
    // Note that we only show the spinner on the left if an icon is displayed.
    // But on the right side, we do display a spinner, even if no icon is set.
    if (!props.iconLeft) {
      return false
    }

    if (isSubmitting.value) {
      return Spinner
    }
    else {
      return props.iconLeft
    }
  })

  const displayedIconRight = computed(() => {
    // We show a spinner even if this does not have an icon set, but only if
    // no icon is shown on the left. In this case the spinner would also be
    // displayed left of the text.
    let result
    if (!props.iconLeft && isSubmitting.value) {
      result = Spinner
    }
    else {
      result = props.iconRight
    }
    return result
  })

  const extraIconClasses = computed(() => {
    let classes = [`button__icon-${props.iconRight ? 'right' : 'left'}`]
    return classes.concat(props.iconClasses)
  })

  const isDisabled = computed(() => {
    // if false is returned, the attribute is still rendered and picked up by the css as disabled

    if (props.disabled) {
      return true
    }
    if (!props.disableOnSubmit) {
      return null
    }

    return isSubmitting.value
      ? true
      : null;
  })
</script>

<style lang="scss" scoped>
  .button {
    .icon {
      width: 1em;
      vertical-align: middle;
      height: 1em;

      &--bolt {
        width: 0.7em;
      }
    }

    &--small {
      .icon {
        width: 0.8em;
        vertical-align: middle;
        line-height: 1em;
      }
    }
  }
</style>
